import { useImportProgressStore } from "../../../Stores/importProgress";
import { useUserStore } from "../../../Stores/User";
import { useWorkspace } from "../../../Stores/Workspaces";
import utilities from "../../../Helpers/utilities";
import {
    buildCompletedImportShiftbarNotice,
    buildImportCompletionLabel,
} from "../../../Helpers/importCompletionNotice";

const recentlyNotifiedCompletedImportIds = new Set();
const COMPLETED_IMPORT_NOTIFICATION_DEDUPE_MS = 10_000;

function buildProgressPayload(data) {
    return {
        id: data.id,
        name:
            data.type === "work-panel-import-trello"
                ? "Trello Import"
                : data.type === "work-panel-import-custom"
                  ? "Custom Import"
                  : data.type === "work-panel-import-jira"
                    ? "Jira Import"
                    : data.type === "work-panel-import-jira-json"
                      ? "Jira Automation"
                      : data.type === "work-panel-import-slack"
                        ? "Slack Import"
                        : data.type === "work-panel-import-asana"
                          ? "Asana Import"
                          : data.type === "work-panel-import-asana-json"
                            ? "Asana Automation"
                            : data.type === "work-panel-import-prospus"
                              ? "Kaamfu Import"
                              : "Import Job",
        progress: data.progress,
        status: data.status || "queued",
        meta: data.meta || { item: { total: 0, imported: 0 } },
        created_at: data.created_at || new Date().toISOString(),
        updated_at: data.updated_at || new Date().toISOString(),
        type: data.type,
        total: data.meta?.item?.total || 0,
        imported: data.meta?.item?.imported || 0,
        counts: data.meta?.counts || {
            boards: { total: 0, imported: 0 },
            items: { total: 0, imported: 0 },
            messages: { total: 0, imported: 0 },
            associates: { total: 0, imported: 0 },
        },
        filename: data.filename ?? null,
    };
}

/** `import-progress.{id}` channel actions. */
export function useImportProgressChannel() {
    function onProgressUpdated(data) {
        const userStore = useUserStore();
        const updatedProgress = buildProgressPayload(data);

        const idx = userStore.initiated_progresses.findIndex(
            (p) => p.id === data.id,
        );

        if (data.status === "completed" || data.status === "error") {
            if (idx !== -1) {
                userStore.initiated_progresses.splice(idx, 1);
            }

            const importLabel = buildImportCompletionLabel(
                updatedProgress,
                data.filename,
            );

            if (data.status === "completed") {
                if (!recentlyNotifiedCompletedImportIds.has(data.id)) {
                    recentlyNotifiedCompletedImportIds.add(data.id);
                    setTimeout(() => {
                        recentlyNotifiedCompletedImportIds.delete(data.id);
                    }, COMPLETED_IMPORT_NOTIFICATION_DEDUPE_MS);

                    utilities().showInfoSnackbar(
                        `Import successfully completed: ${importLabel}`,
                    );

                    useWorkspace().showCompletedImportShiftbarNotice(
                        buildCompletedImportShiftbarNotice(updatedProgress),
                    );
                }
            }

            if (data.status === "error") {

                const message = `Import failed: ${importLabel}`;

                const openImportProgressDetail = () => {
                    useImportProgressStore().openProgressDetail(
                        updatedProgress.id,
                        updatedProgress.type,
                    );
                };

                utilities().showErrorSnackbar(
                    message,
                    null,
                    "error",
                    3000,
                    null,
                    null,
                    openImportProgressDetail,
                );
            }

            userStore.addCompletedProgress(updatedProgress);
            return { done: true };
        }

        if (idx !== -1) {
            Object.assign(userStore.initiated_progresses[idx], updatedProgress);
        } else {
            userStore.addInitiatedProgress(updatedProgress);
        }

        return { done: false };
    }

    return {
        onProgressUpdated,
    };
}
